/**
 * SASADM-0171 — the per-app custom hostname (a saas-auth standalone hostname: an APP row on a
 * domain other than the tenant's white-label domain; handoff "standalone tenant hostnames",
 * all routes already live). Opened from the URL table's APP rows (`TenantHostnames`).
 *
 * Set creates the host beside the white-label one; change creates the new host first, then
 * removes the old one (a failed remove keeps both and tells the parent); revert removes the
 * custom host — the white-label host is never touched. A new standalone host is inactive until
 * its DNS verifies (Check DNS in the table). Never offers Set primary (`PRIMARY_IS_DERIVED`).
 */
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { apiAdmin } from '@/api/admin';
import type { DnsCheckResult } from '@/api/admin/hostnames';
import { ApiError } from '@/api/errors';
import type { HostnameDto, RoutingDeployOutcome } from '@/api/types';
import { useCopy } from '@/hooks/useCopy';
import { ErrorBanner } from '@/components/ErrorBanner';
import { CopyToClipboardButton } from '@/components/CopyToClipboardButton';
import { normalizeHostname, validateStandaloneHostname } from './hostnameInput';

export interface CustomHostnameDialogProps {
  open: boolean;
  tenantId: number;
  appId: number;
  /** e.g. "App: The Money Meeting Website" */
  appLabel: string;
  /** What the row serves today, with https:// — the custom one if set, else the white-label one, else the system one. */
  currentUrl: string | null;
  /** The existing custom row, if any. */
  custom: HostnameDto | null;
  wlDomainName: string | null;
  /** The tenant's SYSTEM hostnames (to refuse a collision client-side). */
  systemHostnames: string[];
  onClose: () => void;
  /** `oldKept` — a change whose old host couldn't be removed (the new one was added). */
  onChanged: (
    outcome: RoutingDeployOutcome | null,
    kind: 'set' | 'reverted',
    oldKept?: string,
  ) => void;
  /** SASADM-0176 — a revert refused with HOSTNAME_IS_REDIRECT_TARGET; the parent shows the notice. */
  onRedirectTarget?: (err: ApiError) => void;
}

export function CustomHostnameDialog({
  open,
  tenantId,
  appId,
  appLabel,
  currentUrl,
  custom,
  wlDomainName,
  systemHostnames,
  onClose,
  onChanged,
  onRedirectTarget,
}: CustomHostnameDialogProps) {
  const { t } = useCopy();
  const [value, setValue] = useState(custom?.hostname ?? '');
  // FRM-005 — client errors show only after the first Save or DNS check attempt.
  const [attempted, setAttempted] = useState(false);
  const [serverFieldError, setServerFieldError] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [dns, setDns] = useState<{ host: string; result: DnsCheckResult } | null>(null);
  const [confirmRevert, setConfirmRevert] = useState(false);
  const [pending, setPending] = useState<'save' | 'revert' | null>(null);
  // Guards a second submit before the disabled state re-renders (FRM-014).
  const inFlight = useRef(false);

  // Reset the draft on every open (adjusting state on a prop change, not in an effect).
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setValue(custom?.hostname ?? '');
      setAttempted(false);
      setServerFieldError(null);
      setDialogError(null);
      setDns(null);
      setConfirmRevert(false);
    }
  }

  const trimmed = normalizeHostname(value);
  const clientError = validateStandaloneHostname(trimmed, wlDomainName, systemHostnames, t);
  const fieldError = serverFieldError ?? (attempted ? clientError : null);

  // Advisory: a failed check shows nothing.
  async function runDnsCheck(host: string) {
    try {
      const result = await apiAdmin.hostnames.dnsCheck(host);
      setDns({ host, result });
    } catch {
      // ignore
    }
  }

  // The prefilled custom host gets its DNS line when the dialog opens.
  const prefill = open ? (custom?.hostname ?? null) : null;
  useEffect(() => {
    if (prefill === null) return;
    let cancelled = false;
    void (async () => {
      try {
        const result = await apiAdmin.hostnames.dnsCheck(prefill);
        if (!cancelled) setDns({ host: prefill, result });
      } catch {
        // Advisory: a failed check shows nothing.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [prefill]);

  function handleBlur() {
    if (!trimmed) return;
    setAttempted(true);
    if (clientError === null && dns?.host !== trimmed) void runDnsCheck(trimmed);
  }

  async function handleSave() {
    if (inFlight.current) return;
    setAttempted(true);
    if (clientError !== null || trimmed === custom?.hostname) return;
    inFlight.current = true;
    setPending('save');
    setServerFieldError(null);
    setDialogError(null);
    try {
      const created = await apiAdmin.hostnames.create(tenantId, {
        hostname: trimmed,
        hostnameType: 'APP',
        appId,
      });
      let oldKept: string | undefined;
      if (custom) {
        try {
          await apiAdmin.hostnames.delete(tenantId, custom.id);
        } catch {
          oldKept = custom.hostname;
        }
      }
      // The create deployed first: report its outcome.
      onChanged(created.routingDeploy ?? null, 'set', oldKept);
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'INVALID_HOSTNAME') {
          setServerFieldError(
            err.details.message ?? t('errors.codes.INVALID_HOSTNAME', 'Enter a valid hostname.'),
          );
        } else if (err.code === 'HOSTNAME_ALREADY_REGISTERED') {
          setServerFieldError(
            t('hostnames.custom.taken', 'That hostname is already used by a tenant.'),
          );
        } else {
          setDialogError(err.details.message ?? t(`errors.codes.${err.code}`, err.code));
        }
      }
    } finally {
      inFlight.current = false;
      setPending(null);
    }
  }

  async function handleRevert() {
    if (!custom || inFlight.current) return;
    inFlight.current = true;
    setPending('revert');
    setDialogError(null);
    try {
      const outcome = await apiAdmin.hostnames.delete(tenantId, custom.id);
      onChanged(outcome, 'reverted');
      onClose();
    } catch (err) {
      setConfirmRevert(false);
      if (
        err instanceof ApiError &&
        err.code === 'HOSTNAME_IS_REDIRECT_TARGET' &&
        onRedirectTarget
      ) {
        onRedirectTarget(err);
      } else if (err instanceof ApiError) {
        setDialogError(err.details.message ?? t(`errors.codes.${err.code}`, err.code));
      }
    } finally {
      inFlight.current = false;
      setPending(null);
    }
  }

  const busy = pending !== null;
  const dnsLine = dns && dns.host === trimmed ? dns.result : null;
  const target = dnsLine?.expectedTargets[0];

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="custom-hostname-dialog-title"
    >
      <DialogTitle id="custom-hostname-dialog-title">
        {t('hostnames.custom.title', 'Custom hostname — {{app}}', { app: appLabel })}
      </DialogTitle>
      <DialogContent>
        {dialogError && <ErrorBanner message={dialogError} />}
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t('hostnames.custom.current', 'Currently served at')}{' '}
          <Box component="span" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {currentUrl ?? '—'}
          </Box>
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          {t(
            'hostnames.custom.help',
            "Serve this app on a hostname from another domain, e.g. a marketing site at www.example.ai, while sign-in stays on the white-label domain. It needs its own CNAME and DNS check. Pages on another domain can link to sign-in but can't silently tell who is signed in.",
          )}
        </Typography>
        <TextField
          fullWidth
          label={t('hostnames.custom.field', 'Hostname')}
          value={value}
          onChange={(e) => {
            setValue(e.target.value.toLowerCase());
            setServerFieldError(null);
          }}
          onBlur={handleBlur}
          error={fieldError !== null}
          helperText={fieldError}
          disabled={busy}
          slotProps={{ htmlInput: { sx: { fontFamily: 'monospace' }, spellCheck: false } }}
        />
        {dnsLine && dnsLine.configured === false ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1.5 }}>
            {t(
              'hostnames.custom.dnsNotConfigured',
              "DNS checking isn't configured on saas-auth (an ops task).",
            )}
          </Typography>
        ) : dnsLine && target ? (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mt: 1.5 }}>
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              {t('hostnames.custom.cname', 'Create a CNAME: {{host}} → {{target}}', {
                host: trimmed,
                target,
              })}
            </Typography>
            <CopyToClipboardButton value={target} />
          </Stack>
        ) : null}
        {confirmRevert && custom && (
          <Alert
            severity="warning"
            sx={{ mt: 2 }}
            action={
              <Stack direction="row" spacing={1}>
                <Button
                  color="inherit"
                  size="small"
                  disabled={busy}
                  startIcon={
                    pending === 'revert' ? <CircularProgress size={16} color="inherit" /> : null
                  }
                  onClick={() => void handleRevert()}
                >
                  {t('hostnames.custom.revertConfirmAction', 'Confirm')}
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  disabled={busy}
                  onClick={() => setConfirmRevert(false)}
                >
                  {t('common.cancel', 'Cancel')}
                </Button>
              </Stack>
            }
          >
            {t(
              'hostnames.custom.revertConfirm',
              'Remove {{host}}? The app stays on its white-label URL.',
              {
                host: custom.hostname,
              },
            )}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {custom && (
          <Button
            color="error"
            disabled={busy || confirmRevert}
            onClick={() => setConfirmRevert(true)}
            sx={{ mr: 'auto' }}
          >
            {t('hostnames.custom.revert', 'Revert to white-label domain')}
          </Button>
        )}
        <Button onClick={onClose} disabled={busy}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={busy || (custom != null && trimmed === custom.hostname)}
          startIcon={pending === 'save' ? <CircularProgress size={16} color="inherit" /> : null}
          onClick={() => void handleSave()}
        >
          {t('common.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
